import React from 'react';
import { useIntl } from 'react-intl';
import classNames, { Argument } from 'classnames';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';

import { useSelector } from '../../../hooks';
import { isLowPerfMode } from 'modules/app/selectors';

import Icon from '../Icon';
import Button, { ButtonType } from '../Button';
import ShareboxEmergencyAccess from '../ShareboxEmergencyAccess';

export interface ModalProps {
  open: boolean;
  className?: Argument;
  title?: React.ReactNode;
  onClose?: (value: boolean) => void;
  focusRef?: React.RefObject<HTMLElement | null>;
  // Use to hide the close button but still keep the outside click behavior to close the modal.
  hideCloseButton?: boolean;
  outsideClickClose?: boolean;
  role?: 'dialog' | 'alertdialog';
}

const dummyCloseFunction = () => { };

export const wrapperTransitions: Record<string, string> = {
  leaveTo: 'opacity-0',
  enterFrom: 'opacity-0',
  enterTo: 'opacity-100',
  leaveFrom: 'opacity-100',
  enter: 'ease-out duration-300',
  leave: 'ease-in duration-200',
};

const contentTransitions = {
  leave: 'ease-in duration-200',
  leaveTo: 'opacity-0 scale-95',
  enter: 'ease-out duration-300',
  enterFrom: 'opacity-0 scale-95',
  enterTo: 'opacity-100 scale-100',
  leaveFrom: 'opacity-100 scale-100',
};

const Modal: React.FC<React.PropsWithChildren<ModalProps>> = ({
  children, open, role, className, title, onClose, focusRef, hideCloseButton, outsideClickClose = true,
}) => {
  const intl = useIntl();
  const lowPerfMode = useSelector(isLowPerfMode);

  const handleClick = () => onClose(false);
  const showCloseButton = !hideCloseButton && typeof onClose === 'function';
  const outsideClickCloseFn = outsideClickClose && typeof onClose === 'function' ? onClose : dummyCloseFunction;

  return (
    <Transition appear show={open}>
      <Dialog as="div" className="relative z-60" onClose={outsideClickCloseFn} initialFocus={focusRef} role={role}>
        <TransitionChild
          data-testid="modal-transition"
          {...(!lowPerfMode ? wrapperTransitions : {})}
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </TransitionChild>
        <div className="fixed inset-0 overflow-y-auto" data-testid="modal">
          <ShareboxEmergencyAccess />
          <div className="flex min-h-full items-end md:items-center justify-center text-center">
            <TransitionChild
              {...(!lowPerfMode ? contentTransitions : {})}
            >
              <DialogPanel
                className={classNames(
                  'transform overflow-hidden rounded-t-modal md:rounded-modal bg-white p-l text-left align-middle shadow-xl transition-all',
                  className,
                  { 'pt-4 kioskSharebox:pt-6 kiosk:pt-8': typeof onClose === 'function' },
                )}
              >
                <DialogTitle
                  as="div"
                  className={classNames({
                    'px-12 kiosk:px-18': showCloseButton,
                    'mb-l': Boolean(children),
                  }, 'relative text-center')}
                >
                  {title}
                  {showCloseButton && (
                    <Button
                      type={ButtonType.LIGHT}
                      onClick={handleClick}
                      testId="closeModal"
                      className="absolute top-0 right-0 flex !p-2 ml-auto kiosk:!p-4 kioskSharebox:!p-0"
                      aria-label={intl.formatMessage({ id: 'close', defaultMessage: 'Close' })}
                    >
                      <Icon className="fb-icon--close text-2xl kiosk:text-4xl text-default leading-none" />
                    </Button>
                  )}
                </DialogTitle>
                {children}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default Modal;
